/**
 * catalogue.ts — client for GET /portal/catalogue (ADR-085 D1).
 *
 * The one price list every surface reads: products, the three terms at rack,
 * and the EA overlay — resolved server-side so no client ever hardcodes a
 * price. Consumed by the Subscription page, the Change Plan modal, and the
 * OBW subscribe step.
 *
 * The endpoint is T0 PUBLIC (pricing is public marketing data) and this client
 * deliberately uses a PLAIN fetch with no auth header: the OBW subscribe step
 * renders on the public, unauthenticated sign-up mount, where no Cognito
 * session exists.
 *
 * ⭐ THE CATALOGUE IS PER-ZONE DATA (ADR-120 P2, ADR-124 D7). Each zone holds its
 * own `Nexa-Catalogue`, forked from the home zone's price list when the zone is
 * stood up and free to diverge afterwards. So the list is read from the zone the
 * session is talking to — `activePortalApiBaseUrl()` — never from the bundle's
 * build-time origin: a signed-in US customer's plan step, Subscription page and
 * Change Plan modal all price from the US table. Before sign-in (the public
 * sign-up mount) the active zone is still the build zone, which is the pre-
 * existing behaviour; the residency step (ADR-124 D1, S1 build) is what moves a
 * NEW customer's session onto their own zone before the plan step renders.
 *
 * ⚠ AND SO THE CACHES ARE KEYED BY ZONE. The session cache and the module-level
 * promise both existed before the catalogue was per-zone, and both would have
 * quietly served the AU list to a US customer who reached sign-in on the same
 * page load (or the same tab) after the public mount had already cached AU's
 * prices. A zone-keyed cache cannot: a change of active zone is a cache miss.
 */
import { activePortalApiBaseUrl, activeZone } from './zone-session';
import type { CatalogueDiscount } from './billing-display';

export type CatalogueProductId = 'nexagrowth' | 'nexasuccess' | 'nexacontext';
export type CatalogueTerm = 'monthly' | 'quarterly' | 'annual';
export type TierCode = 'T1' | 'T2' | 'T3';

/** Per-term price row. `sub` is the full charge for the term (ex-GST);
 *  `monthlyEquivalent` is sub spread per month for display. The optional `ea`
 *  overlay is the Early Adopter price for the same term. */
export interface CatalogueTermPricing {
  sub: number;
  monthlyEquivalent: number;
  ea?: {
    sub: number;
    monthlyEquivalent: number;
    durationMonths: number;
  };
  /** PL28 — generic, data-driven applied-discount detail for the breakdown
   *  modal (label + % + discounted price), server-resolved. Present when a
   *  discount applies to this term (EA today; a partner special later). */
  discount?: CatalogueDiscount;
}

export interface CatalogueProduct {
  product: CatalogueProductId;
  name: string;
  tierCode: TierCode;
  /** false = defined-not-priced (NexaContext) — surfaces as "Coming soon". */
  priced: boolean;
  /** Empty object when priced === false. */
  terms: Partial<Record<CatalogueTerm, CatalogueTermPricing>>;
}

export interface Catalogue {
  products: CatalogueProduct[];
  currency: string;
  taxNote?: string;
  /** PL37 (C4) — a stable content version for session caching + invalidation.
   *  The catalogue is identical for all users OF A ZONE and changes rarely; the
   *  client caches it for the session and busts when this version changes. */
  version?: string;
}

// PL37 (C4) — session cache. The catalogue is the ONE thing safe to cache
// client-side (identical for every user of a zone, changes rarely, carries a
// version). It lives in sessionStorage so it survives an in-session
// navigation/reload, keyed by the ZONE it was read from and by nothing per-user.
// NEVER cache account/domain/subscription/summary here — that is per-user state
// that mutates on user actions (a stale cache there is the "my fix isn't
// showing" bug the brief forbids).
const SESSION_KEY_PREFIX = 'nexa.catalogue.v2:';

const sessionKey = (zone: string): string => `${SESSION_KEY_PREFIX}${zone}`;

function readSession(zone: string): Catalogue | null {
  try {
    const raw = sessionStorage.getItem(sessionKey(zone));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Catalogue;
    return Array.isArray(parsed?.products) ? parsed : null;
  } catch {
    return null;
  }
}

function writeSession(zone: string, cat: Catalogue): void {
  try {
    sessionStorage.setItem(sessionKey(zone), JSON.stringify(cat));
  } catch {
    /* quota/denied — non-fatal, the module cache still serves this page load */
  }
}

// Module-level promise cache — the catalogue is fetched at most once per page
// load PER ZONE and shared by every consumer (Subscription page, Change Plan
// modal, OBW subscribe step). A failed fetch clears that zone's entry so a
// later mount can retry rather than being pinned to the failure forever.
const cataloguePromises = new Map<string, Promise<Catalogue>>();

export function fetchCatalogue(): Promise<Catalogue> {
  const zone = activeZone().zone;
  let promise = cataloguePromises.get(zone);
  if (!promise) {
    // Session cache hit — serve without a network call (C4). The consolidated
    // Manage Account load primes this, so Subscription/Change-Plan/OBW skip the
    // catalogue fetch entirely within a session.
    const cached = readSession(zone);
    if (cached) {
      promise = Promise.resolve(cached);
      cataloguePromises.set(zone, promise);
      return promise;
    }
    promise = (async () => {
      const res = await fetch(`${activePortalApiBaseUrl()}/portal/catalogue`);
      if (!res.ok) {
        throw new Error(`catalogue fetch failed: HTTP ${res.status}`);
      }
      const body = (await res.json()) as Catalogue;
      if (!Array.isArray(body?.products)) {
        throw new Error('catalogue fetch failed: malformed body');
      }
      writeSession(zone, body); // cache for the session
      return body;
    })().catch((err: unknown) => {
      cataloguePromises.delete(zone); // allow retry on the next call
      throw err;
    });
    cataloguePromises.set(zone, promise);
  }
  return promise;
}

/** PL37 (C4) — seed the session catalogue cache from a payload already fetched
 *  (the consolidated Manage Account response, which came from the ACTIVE zone's
 *  API and is therefore that zone's list). Version-aware: replaces the cache
 *  when the version differs so an edited catalogue busts it. Ignores a
 *  malformed or absent payload (the caller degrades to a later fetch). */
export function primeCatalogueCache(catalogue: Catalogue | null | undefined): void {
  if (!catalogue || !Array.isArray(catalogue.products)) return;
  const zone = activeZone().zone;
  const existing = readSession(zone);
  if (existing?.version && catalogue.version && existing.version === catalogue.version) return;
  writeSession(zone, catalogue);
  cataloguePromises.set(zone, Promise.resolve(catalogue));
}

/** Test seam — clears the module + session cache (every zone) so each test
 *  starts cold. */
export function resetCatalogueCache(): void {
  cataloguePromises.clear();
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(SESSION_KEY_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

/** Find a product row by its id. */
export function catalogueProduct(
  cat: Catalogue,
  product: CatalogueProductId,
): CatalogueProduct | undefined {
  return cat.products.find((p) => p.product === product);
}

/** Find a product row by tier code (the OBW subscribe step keys on tiers). */
export function catalogueProductByTier(
  cat: Catalogue,
  tierCode: TierCode,
): CatalogueProduct | undefined {
  return cat.products.find((p) => p.tierCode === tierCode);
}

/**
 * Resolve the displayable price for a product + term, honouring the EA overlay
 * when `withEa` is set (e.g. the domain's billing.promoCode === 'EA').
 * Returns null when the product/term is unpriced (NexaContext, absent rows).
 */
export function resolveTermPrice(
  cat: Catalogue,
  product: CatalogueProductId,
  term: CatalogueTerm,
  opts: { withEa?: boolean } = {},
): { sub: number; monthlyEquivalent: number; isEa: boolean } | null {
  const row = catalogueProduct(cat, product);
  const t = row?.priced ? row.terms[term] : undefined;
  if (!t) return null;
  if (opts.withEa && t.ea) {
    return { sub: t.ea.sub, monthlyEquivalent: t.ea.monthlyEquivalent, isEa: true };
  }
  return { sub: t.sub, monthlyEquivalent: t.monthlyEquivalent, isEa: false };
}
